import { CookiesProvider } from "react-cookie";
import ReactDOM from "react-dom/client";

import { fireEarlyCheckoutTracking } from "@lib/analytics/earlyCheckoutTracking";
import { prefetchCheckoutData } from "@lib/checkout/checkoutPrefetch";
import { Provider as AppProvider } from "@lib/contexts/AppContext";
import { ScreenProvider } from "@lib/contexts/ScreenProvider.tsx";
import { SegmentProvider } from "@lib/contexts/SegmentContext.tsx";
import { prefetchMemberSession, preloadHeroForPath } from "@lib/memberPrefetch";
import { installDomNodeGuards } from "@utils/domNodeGuards";

import App from "./App.tsx";
import i18n, { loadTechniquesNamespace } from "./i18n.ts";
import {
  isAuthRoute,
  needsTechniquesNamespace,
  prefetchForPath,
  whenIdle,
} from "./routeChunks";

import "./styles/globals.css";

// Guard Node.prototype.removeChild/insertBefore before React mounts:
// Chrome auto-translate (and some extensions) re-parent React-owned text
// nodes, and React 18's reconciler then throws NotFoundError and crash-loops
// the tree. /checkout is the worst-affected page (Sentry NMH-REACT-34N,
// replay-confirmed buyer who never saw the page load — OMG-2136). See
// domNodeGuards.ts for the mechanism.
installDomNodeGuards();

// Fire add_to_cart/select_item/begin_checkout on /checkout as soon
// as this module runs — before we await i18n init and before React
// mounts. Closes the funnel-timing gap vs the legacy /doSignup, which
// fires these from a server-rendered Angular controller within
// ~500ms of navigation. See earlyCheckoutTracking.ts for the full
// reasoning + tradeoffs. Sets `window.__omgyesEarlyCheckoutFired` so
// the React-side useEffect in checkout.tsx skips the duplicate fire
// on the same page load.
fireEarlyCheckoutTracking();

// Dispatch the pricing (and discount) request before the i18n await and
// before React mounts, hydrating the module-level react-query cache — the
// Render cold-start then overlaps app boot instead of starting after it.
// See checkoutPrefetch.ts for the fallback contract.
prefetchCheckoutData();

// Start downloading the lazy chunk(s) the current URL renders with, plus the
// Contentful provider stack and its member-home entry on member routes, so
// they overlap the i18n await and the auth check instead of queueing behind
// them. See routeChunks.ts for the waterfall this collapses (OMG-1836).
prefetchForPath(window.location.pathname);

// Member routes only: warm the four omg-api queries the providers run after
// mount (getMe, user-preferences, experiments, CloudFront cookies) from the
// Amplify session in localStorage, and preload the hero image as soon as the
// Contentful entry that names it lands. See memberPrefetch.ts (OMG-1836).
prefetchMemberSession(window.location.pathname);
preloadHeroForPath(window.location.pathname);

// techniques.json is no longer part of the i18n init (see i18n.ts). The
// pages behind RequireTechniquesNamespace in routes.tsx need it, so start
// the download now, alongside the prefetches above; /post-checkout is a
// member route that never reads it and hard-reloads into /members/ (which
// re-inits i18n with cache: "no-store"), so it is left out. The login page
// does not need it either, but the /home it signs into does: warm it there
// once the form is on screen and the browser is idle (OMG-1836).
if (needsTechniquesNamespace(window.location.pathname)) {
  void loadTechniquesNamespace();
} else if (isAuthRoute(window.location.pathname)) {
  whenIdle(() => void loadTechniquesNamespace());
}

const country = "US";

// `RecoilRoot` and `VideoProvider` were both previously wrapped at
// this top level. Both were dead weight on /checkout (and on every
// non-member page):
//
// - `RecoilRoot` (from `recoil`) is only required by hooks in
//   `src/pages/admin/*` — and those routes aren't wired into this
//   app's `routes.tsx` at all. Recoil is effectively unreachable
//   code in this build. Removing the wrapper means Rollup no
//   longer generates the `recoil-*.js` chunk at all (verified:
//   `dist/assets/recoil-*` is no longer emitted), saving ~19 KiB
//   gzip download on every page that previously fetched the chunk
//   via the entry's dynamic-import dependency graph, plus the
//   `RecoilRoot` initialization cost.
//
// - `VideoProvider` (from `VideoContextOld`) is only consumed by
//   `VideoPlayerOld.tsx`, which is rendered inside member-area
//   article/animation/datenight pages — all reachable only through
//   the lazy-loaded member routes. Moved the Provider into the
//   member-routes block in routes.tsx so /checkout, /gifts,
//   /redeem, /post-checkout, and /login don't pay the cost.
const tree = (
  // <React.StrictMode>
  <AppProvider country={country} bannerActive={false}>
    <SegmentProvider>
      <CookiesProvider
        defaultSetOptions={{
          path: "/",
          domain: ["dev", "local"].includes(import.meta.env.VITE_ENV)
            ? "localhost"
            : ".omgyes.com",
          sameSite: "lax",
        }}
      >
        <ScreenProvider>
          <App />
        </ScreenProvider>
      </CookiesProvider>
    </SegmentProvider>
  </AppProvider>
  // </React.StrictMode>,
);

// Wait for i18next to finish loading its default-namespace translations
// before mounting React. Without this, useTranslation() throws on the
// first render, which propagates up to the outer <React.Suspense
// fallback={<Fallback />}> in App.tsx and renders the fullscreen branded
// loader on top of the inline HTML skeleton (added in #737). The result
// on /checkout is a "skeleton → fullscreen flash → real form" sequence
// that users perceive as the page glitching. By awaiting i18next here
// the skeleton stays visible until React commits with translations
// ready, so the user sees a single skeleton → real form transition.
//
// Safety: if i18next fails to load or stalls (network down, origin
// rejecting, etc.), mount React anyway after a short timeout so the
// page isn't blocked entirely. Worst case: components render with
// their translation keys as fallback text. Better than a permanently
// visible skeleton.
const I18N_MOUNT_TIMEOUT_MS = 3000;

const mountReact = () => {
  ReactDOM.createRoot(document.getElementById("root")!).render(tree);
};

if (i18n.isInitialized) {
  mountReact();
} else {
  let mounted = false;
  const mountOnce = () => {
    if (mounted) return;
    mounted = true;
    mountReact();
  };
  i18n.on("initialized", mountOnce);
  setTimeout(mountOnce, I18N_MOUNT_TIMEOUT_MS);
}
